import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AdminDashboard } from "@/components/admin/AdminDashboard";
import { Starfield } from "@/components/Starfield";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, ArrowLeft, Leaf } from "lucide-react";
import { toast } from "sonner";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

const LoginScreen = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Connexion réussie");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grain flex flex-col items-center justify-center px-4">
      <Starfield />
      <Link
        to="/"
        data-testid="back-to-store-link"
        className="absolute top-5 left-5 z-10 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Boutique
      </Link>
      <div className="relative z-10 w-full max-w-sm bg-card border border-border rounded-3xl p-8 float-up shadow-sm">
        <div className="h-12 w-12 rounded-2xl brand-terracotta flex items-center justify-center mx-auto mb-4">
          <Lock className="h-6 w-6 text-white" />
        </div>
        <h1 className="font-heading text-2xl font-bold text-center">Espace Admin</h1>
        <p className="text-sm text-muted-foreground text-center mt-1 mb-6">
          Gérez votre catalogue et vos réglages
        </p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              data-testid="admin-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@atelier.fr"
              className="mt-1"
              required
            />
          </div>
          <div>
            <Label htmlFor="password">Mot de passe</Label>
            <Input
              id="password"
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="mt-1"
              required
            />
          </div>
          <button
            data-testid="admin-login-button"
            disabled={loading}
            className="brand-terracotta text-white w-full h-11 rounded-full font-semibold active:scale-95 transition-transform disabled:opacity-60"
          >
            {loading ? "Connexion…" : "Se connecter"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default function Admin() {
  const { user, ready } = useAuth();

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Chargement…
      </div>
    );
  }
  if (!user) return <LoginScreen />;
  return <AdminDashboard />;
}
